import State from './State.js'
import Renderer from '../render/Renderer.js'
import Camera from '../render/Camera.js'
import InteractionSystem from '../render/InteractionSystem.js'
import DebugOverlay from '../debug/DebugOverlay.js'
import GenomeControls from '../ui/GenomeControls.js'
import EvolutionLabPanel from '../ui/EvolutionLabPanel.js'
import ClusterInspector from '../ui/ClusterInspector.js'
import VibeEngine from '../systems/VibeEngine.js'
import GenomeEngine from '../systems/GenomeEngine.js'
import ClusterDetection from '../systems/ClusterDetection.js'
import SymbolicTerritory from '../systems/SymbolicTerritory.js'
import SymbolicInterpretationEngine from '../systems/SymbolicInterpretationEngine.js'
import SymbolicEvolutionLab from '../systems/SymbolicEvolutionLab.js'
import AtmosphereEngine from '../systems/AtmosphereEngine.js'
import vibeData from '../../data/vibes.json'

export default class App {
  constructor(root) {
    this.root = root
    this.frameHandle = null
    this.lastFrameTime = 0
    this.lastSimTime = 0
    this.simTickRate = 1000 / 20 // 20Hz simulation
    this.state = new State()
   }

  async init() {
    this.vibeEngine = new VibeEngine()
    await this.vibeEngine.load(vibeData)

    this.genomeEngine = new GenomeEngine()
    this.clusterDetection = new ClusterDetection()
    this.symbolicTerritory = new SymbolicTerritory()
    this.symbolicInterpretationEngine = new SymbolicInterpretationEngine()
    this.symbolicEvolutionLab = new SymbolicEvolutionLab()
    this.renderer = new Renderer()
    this.renderer.init(this.root)

    this.camera = new Camera()
    this.camera.setViewport(this.renderer.getViewport())
    this.renderer.setCamera(this.camera)

    this.atmosphereEngine = new AtmosphereEngine()
    this.interactionSystem = new InteractionSystem()
    this.debugOverlay = new DebugOverlay()
    this.debugOverlay.init(this.root)

    this.controlsPanel = new GenomeControls()
    this.controlsPanel.init(this.root, {
      state: this.state,
      onChange: this.handleDynamicsChange
     })

    this.evolutionLabPanel = new EvolutionLabPanel()
    this.evolutionLabPanel.init(this.root, {
      lab: this.symbolicEvolutionLab,
      onStateChange: this.handleEvolutionLabChange
     })

    this.clusterInspector = new ClusterInspector()
    this.clusterInspector.init(this.root)

    this.interactionSystem.init({
      element: this.renderer.getCanvas(),
      camera: this.camera,
      getVibes: () => this.vibeEngine.getVibes(),
      getTerritories: () => this.symbolicInterpretationEngine.getTerritories(),
      onHover: this.handleHover,
      onSelect: this.handleSelect,
      onSelectCluster: this.handleSelectCluster
     })

    this.refreshRegions(this.vibeEngine.getVibes())
    this.atmosphereEngine.update(this.state, this.vibeEngine.getVibes(), 0.016, 0)

    this.frameHandle = requestAnimationFrame(this.loop)
   }

  handleHover = (vibe) => {
    this.state.setHoveredVibe(vibe)
   }

  handleSelect = (vibe, options = {}) => {
    this.state.setSelectedVibe(vibe, options)

    if (vibe) {
      this.state.setSelectedCluster(null)
      return
     }

    if (!options.preserveClusterSelection) {
      this.state.setSelectedCluster(null)
     }
   }

  handleSelectCluster = (territory) => {
    if (!territory) {
      this.state.setSelectedCluster(null)
      return
     }

    if (this.symbolicEvolutionLab.getState().isExperimentalMode) {
      const labState = this.symbolicEvolutionLab.getState()
      const isSelected = labState.selectedTerritories.has(territory.id)
      this.symbolicEvolutionLab.selectTerritory(territory.id, !isSelected)
      this.evolutionLabPanel.renderValues()
      return
     }

    this.state.clearSelectedVibes()
    this.state.setSelectedCluster(territory.id)
   }

  handleDynamicsChange = (key, value) => {
    this.state.setDynamicsValue(key, value)
   }

  handleEvolutionLabChange = (type, key, value) => {
    switch (type) {
      case 'modifier':
        this.symbolicEvolutionLab.updateState({
          emotionalModifiers: {
             ...this.symbolicEvolutionLab.getState().emotionalModifiers,
             [key]: value
           }
         })
        break
        
      case 'toggle':
        if (key === 'freeze') {
          this.symbolicEvolutionLab.updateState({ isFrozen: value })
         } else if (key === 'experimental') {
          this.symbolicEvolutionLab.updateState({ isExperimentalMode: value })
         }
        break
        
      case 'action':
        if (key === 'reset') {
          this.symbolicEvolutionLab.reset()
          this.evolutionLabPanel.renderValues()
         } else if (key === 'generate-hybrid') {
          const hybrid = this.symbolicEvolutionLab.generateHybrid(
            this.symbolicInterpretationEngine.getTerritories(),
            this.symbolicInterpretationEngine
           )
          if (hybrid) console.log('Generated hybrid:', hybrid)
         }
        break
     }
   }

  refreshRegions(vibes) {
    const modifiedVibes = this.symbolicEvolutionLab.applyGlobalModifiers(vibes)
    
    this.genomeEngine.update(modifiedVibes, this.state.dynamics)
    this.clusterDetection.update(modifiedVibes, this.genomeEngine.getLinks(), this.genomeEngine)
    this.symbolicTerritory.update(this.clusterDetection.getClusters())
    this.symbolicInterpretationEngine.update({
      territories: this.symbolicTerritory.getTerritories(),
      vibes: modifiedVibes,
      genomeEngine: this.genomeEngine,
      clusterAssignments: this.clusterDetection.getClusterAssignments()
     })
    this.syncSelectedCluster()
   }

  syncSelectedCluster() {
    if (this.state.selectedClusterId && !this.symbolicInterpretationEngine.getById(this.state.selectedClusterId)) {
      this.state.setSelectedCluster(null)
     }
   }

  getSelectedInspectorContext() {
    const selectedVibeId = this.state.selectedVibeIds[0]
    const selectedVibe = selectedVibeId
       ? this.vibeEngine.getVibes().find(vibe => vibe.id === selectedVibeId) ?? null
       : null
    const selectedTerritory = this.state.selectedClusterId
       ? this.symbolicInterpretationEngine.getById(this.state.selectedClusterId)
       : null
    const vibeTerritory = selectedVibe
       ? this.symbolicInterpretationEngine.getById(this.clusterDetection.getClusterIdForVibe(selectedVibe.id))
       : null
    const nodeInterpretation = selectedVibe
       ? this.symbolicInterpretationEngine.getNodeInterpretation(selectedVibe.id)
       : null

    const evolutionLabState = this.symbolicEvolutionLab.getState()
    const selectedTerritories = Array.from(evolutionLabState.selectedTerritories)
       .map(id => this.symbolicInterpretationEngine.getById(id))
       .filter(Boolean)

    return {
      selectedVibe,
      selectedTerritory,
      vibeTerritory,
      nodeInterpretation,
      evolutionLabState,
      selectedTerritoriesForHybridization: selectedTerritories
     }
   }

  loop = (time) => {
    const seconds = time * 0.001
    const deltaTime = this.lastFrameTime === 0
       ? 1 / 60
       : Math.min(0.05, seconds - this.lastFrameTime)

    this.lastFrameTime = seconds

     // 1. High-frequency physics/movement (60fps)
    this.vibeEngine.update(seconds, deltaTime, this.genomeEngine.getFieldState(), this.state.dynamics)

     // 2. Low-frequency simulation/topology (20Hz)
    if (seconds - this.lastSimTime > this.simTickRate / 1000) {
      const vibes = this.vibeEngine.getVibes()
      this.refreshRegions(vibes)
      this.lastSimTime = seconds
     }

    const vibes = this.vibeEngine.getVibes()
    this.atmosphereEngine.update(this.state, vibes, deltaTime, seconds)

    const links = this.genomeEngine.getLinks()
    const territories = this.symbolicInterpretationEngine.getTerritories()
    const genomeMetrics = this.genomeEngine.getMetrics()
    const clusterMetrics = this.clusterDetection.getMetrics()
    const atmosphere = this.atmosphereEngine.getState()
    const inspectorContext = this.getSelectedInspectorContext()

    this.renderer.render({
      vibes,
      links,
      territories,
      clusterAssignments: this.clusterDetection.getClusterAssignments(),
      atmosphere,
      state: this.state,
      time: seconds
     })

    this.clusterInspector.update(inspectorContext)
    this.debugOverlay.update({
      fps: Math.round(1 / Math.max(deltaTime, 1 / 240)),
      nodeCount: vibes.length,
      linkCount: links.length,
      selectedCount: this.state.selectedVibeIds.length,
      clusterCount: clusterMetrics.clusterCount,
      averageDistance: genomeMetrics.averageDistance,
      gravityStrength: this.state.dynamics.gravityStrength,
      camera: this.camera
     })

    this.frameHandle = requestAnimationFrame(this.loop)
   }
}